import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  useGetPayrollsQuery,
  useGeneratePayrollMutation,
  useMarkPayrollPaidMutation,
} from '../../../store/api/hrmsApi';

function PayrollDashboard() {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [previewData, setPreviewData] = useState(null);
  
  const monthStr = format(selectedMonth, 'yyyy-MM');

  const { data, isLoading, refetch } = useGetPayrollsQuery({ month: monthStr });
  const payrolls = data?.payrolls || [];

  const [generatePayroll, { isLoading: isGenerating }] = useGeneratePayrollMutation();
  const [markPaid, { isLoading: isMarking }] = useMarkPayrollPaidMutation();

  const handlePreview = async () => {
    try {
      const response = await generatePayroll({ month: monthStr, isPreview: true }).unwrap();
      setPreviewData(response.previewData);
    } catch (error) {
      console.error('Preview generation failed:', error);
      alert(error?.data?.message || 'Failed to generate payroll preview.');
    }
  };

  const handleConfirmSave = async () => {
    if (!window.confirm('Are you sure you want to save this payroll?')) return;
    try {
      await generatePayroll({ month: monthStr }).unwrap();
      alert('Payroll saved successfully!');
      setPreviewData(null);
      refetch();
    } catch (error) {
      console.error('Save failed:', error);
      alert(error?.data?.message || 'Failed to save payroll.');
    }
  };

  const handleCancelPreview = () => {
    setPreviewData(null);
  };

  const handleMarkPaid = async (id) => {
    if (!window.confirm('Are you sure you want to mark this payslip as Paid? This will deduct related advances permanently.')) {
      return;
    }
    try {
      await markPaid(id).unwrap();
      alert('Payslip marked as paid!');
    } catch (error) {
      console.error('Mark paid failed:', error);
      alert(error?.data?.message || 'Failed to mark as paid.');
    }
  };

  const handleDownloadPDF = (row) => {
    const doc = new jsPDF();
    const employeeName = `${row.employee?.firstName || ''} ${row.employee?.lastName || ''}`.trim();
    
    doc.setFontSize(20);
    doc.text('Company Payslip', 14, 22);

    doc.setFontSize(12);
    doc.text(`Employee: ${employeeName}`, 14, 32);
    doc.text(`Month: ${row.month}`, 14, 40);
    doc.text(`Status: ${row.status}`, 14, 48);

    autoTable(doc, {
      startY: 55,
      head: [['Description', 'Amount / Details']],
      body: [
        ['Basic Salary', row.basicSalary],
        ['Present Days', row.presentDays],
        ['Absent Days', row.absentDays],
        ['Half Days', row.halfDays],
        ['Paid Leave Days', row.leaveDays],
        ['Gross Salary', row.grossSalary],
        ['Advance Deductions', row.advanceDeductions > 0 ? `-${row.advanceDeductions}` : '0'],
        ['Net Salary Payable', row.netSalary],
      ],
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`Payslip_${employeeName.replace(/\s+/g, '_')}_${row.month}.pdf`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Payroll Generation
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                views={['year', 'month']}
                label="Select Month"
                value={selectedMonth}
                onChange={(newMonth) => {
                  setSelectedMonth(newMonth || new Date());
                  setPreviewData(null);
                }}
                slotProps={{ textField: { size: 'small' } }}
              />
            </LocalizationProvider>
            
            {!previewData ? (
              <Button 
                variant="contained" 
                color="primary"
                onClick={handlePreview}
                disabled={isGenerating}
              >
                {isGenerating ? 'Generating Preview...' : `Generate Preview for ${format(selectedMonth, 'MMMM yyyy')}`}
              </Button>
            ) : (
              <>
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={handleConfirmSave}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Saving...' : 'Confirm & Save'}
                </Button>
                <Button 
                  variant="outlined" 
                  color="error"
                  onClick={handleCancelPreview}
                  disabled={isGenerating}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>

          {isLoading ? (
            <CircularProgress />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Basic</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Days (P/A/H/L)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Gross</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Advance Ded.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Net Salary</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData ? (
                    previewData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No payroll records generated for this month.
                        </TableCell>
                      </TableRow>
                    ) : (
                      previewData.map((row) => (
                        <TableRow key={row.id} hover sx={{ bgcolor: 'info.lighter' }}>
                          <TableCell>{row.employee?.firstName} {row.employee?.lastName}</TableCell>
                          <TableCell>{row.basicSalary}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {row.presentDays} / {row.absentDays} / {row.halfDays} / {row.leaveDays}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.grossSalary}</TableCell>
                          <TableCell sx={{ color: 'error.main' }}>
                            {row.advanceDeductions > 0 ? `-${row.advanceDeductions}` : '0'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                            {row.netSalary}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label="Preview" color="info" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              Preview only
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )
                  ) : payrolls.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No payroll records found for this month. Click Generate.
                      </TableCell>
                    </TableRow>
                  ) : (
                    payrolls.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.employee?.firstName} {row.employee?.lastName}</TableCell>
                        <TableCell>{row.basicSalary}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {row.presentDays} / {row.absentDays} / {row.halfDays} / {row.leaveDays}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.grossSalary}</TableCell>
                        <TableCell sx={{ color: 'error.main' }}>
                          {row.advanceDeductions > 0 ? `-${row.advanceDeductions}` : '0'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                          {row.netSalary}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            size="small"
                            label={row.status} 
                            color={row.status === 'Paid' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Download Payslip">
                              <IconButton size="small" color="primary" onClick={() => handleDownloadPDF(row)}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {row.status === 'Draft' && (
                              <Button 
                                size="small" 
                                variant="outlined"
                                color="success"
                                onClick={() => handleMarkPaid(row.id)}
                                disabled={isMarking}
                              >
                                Mark Paid
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default PayrollDashboard;
